import React, { useEffect, useState } from "react";
import googleIcon from "../../assets/images/icons/google-icon.svg";
import MainLayout from "../../components/layout/layout";
import {
  LargeHeadline,
  LabelText,
  MediumText,
  SubTitle,
} from "../../components/typography";
import styled from "styled-components";
import { COLORS } from "../../assets/utils/enums/colors";
import Input from "../../components/ui/inputs";
import { CheckboxInput } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { API_URL_AUTH } from "../../constants";
import toast from "react-hot-toast";
import { AuthModeEnum } from "../../assets/utils/validation/types";
import { useAuthValidation } from "../../assets/utils/hooks/useAuthValidation";
import breakpoints from "../../assets/styles/breakpoint";
import CustomToast from "../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import { setAccessToken } from "../../assets/store/auth";
import { useDispatch } from "react-redux";

const SignInPage: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isRemember, setIsRemember] = useState<boolean>(false);

  const [loaderState, setLoaderState] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { errors, validate } = useAuthValidation();

  async function login() {
    try {
      setLoaderState(true);

      const { hasErrors } = validate({ email, password }, AuthModeEnum.LOGIN);
      if (hasErrors) return;

      const response = await fetch(`${API_URL_AUTH}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, isRemember }),
        credentials: "include",
      });

      const data = await response.json();

      if (response.status !== 200) {
        const errorText = await response.text();
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.ERROR}
            text={errorText.split("\n")[0].split("System.Exception:")[1]?.trim() || "Помилка логіну"}
          />
        ));
        return;
      }

      dispatch(setAccessToken(data.accessToken));

      try {
        toast((t) => (
          <CustomToast
            t={t}
            type={
              response.status === 200
                ? ToastTypeEnum.SUCCESS
                : ToastTypeEnum.ERROR
            }
            text={
              response.status === 200
                ? "Успішний авторизація."
                : "Помилка авторизації."
            }
          />
        ));

        navigate("/settings");
      } catch (jsonErr) {
        console.error("Не JSON відповідь:", data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.ERROR}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
        />
      ));
    } finally {
      setTimeout(() => {
        setLoaderState(false);
      }, 1000);
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        login();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  return (
    <MainLayout
      className="d-flex flex-column align-items-center"
      loaderState={loaderState}
    >
      <BodyBlock>
        <LargeHeadline>З поверненням!</LargeHeadline>
        <Input
          attribute="email"
          value={email}
          setValue={setEmail}
          errorText={errors?.email!}
          label="Електронна пошта"
          placeholder="Example@gmail.com"
        />
        <Input
          attribute="password"
          value={password}
          setValue={setPassword}
          errorText={errors?.password!}
          label="Пароль"
          placeholder="Введіть пароль"
        />
        <ItemsSpan className="w-100 d-flex align-items-center justify-content-between">
          <CheckboxInput
            text="Запам’ятати мене"
            state={isRemember}
            setState={setIsRemember}
          />
          <Button
            bgcolor={"transparent"}
            txtcolor={COLORS.blue}
            type="large"
            brcolor={"transparent"}
            width="auto"
            onClick={() => navigate("/forgot-password")}
          >
            <MediumText>Забули пароль</MediumText>
          </Button>
        </ItemsSpan>
        <Button
          bgcolor={COLORS.blue}
          txtcolor={COLORS.white}
          brcolor={"transparent"}
          type="large"
          width="100%"
          onClick={login}
        >
          <LabelText>Увійти</LabelText>
        </Button>
        <Button
          bgcolor={"transparent"}
          txtcolor={COLORS.secondary1}
          brcolor={COLORS["bg-text-color"]}
          type="large"
          width="100%"
          className="d-flex align-items-center justify-content-center gap-2"
          onClick={() =>
            (window.location.href =
              "http://back.galera.fm/api/auth/google/login")
          }
        >
          <img src={googleIcon} alt="google-icon" />
          <LabelText>Увійти за допомогою Google</LabelText>
        </Button>
        <span className="d-flex align-items-center pt-1 cursor-pointer">
          <LightText>Ще не маєте акаунту?</LightText>
          <Button
            bgcolor={"transparent"}
            txtcolor={COLORS.blue}
            type="large"
            brcolor={"transparent"}
            width="auto"
            onClick={() => navigate("/sign-up")}
          >
            <MediumText>Зареєструйтеся</MediumText>
          </Button>
        </span>
      </BodyBlock>
    </MainLayout>
  );
};

export default SignInPage;

export const ItemsSpan = styled.span`
  padding: 0 20px;
  padding-bottom: 10px;

  ${breakpoints.md} {
    padding: 0 8px;
  }

  ${breakpoints.xs} {
    padding: 0;
    padding-left: 8px;
  }
`;

export const BodyBlock = styled.div`
  width: 40%;
  padding: 25px;
  padding-bottom: 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${LargeHeadline} {
    color: ${COLORS.secondary1};
    padding-bottom: 20px;
    text-align: center;
  }

  ${SubTitle} {
    color: ${COLORS["bg-text-color"]};
    text-align: center;
    padding-top: 5px;
    padding-bottom: 15px;
  }

  @media (max-width: 1136px) {
    width: 50%;
  }

  ${breakpoints.lg} {
    width: 60%;
  }

  ${breakpoints.md} {
    width: 75%;
  }

  ${breakpoints.sm} {
    width: 85%;
  }

  ${breakpoints.xs} {
    width: 100%;
    padding: 16px;
  }
`;

export const LightText = styled(MediumText)`
  color: ${COLORS["bg-text-color"]};
  cursor: pointer;
`;
